import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Loader2, Copy, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Reusable "Forward via email" dialog. apiBase is the resource route, e.g. "/documents"
// or "/ap-documents". Sends via Resend (attachment) when configured, otherwise opens the
// user's own email app (mailto) with a secure public download link.
export const ForwardEmailDialog = ({ open, onOpenChange, apiBase, doc, emailCfg, onDone }) => {
  const [form, setForm] = useState({ to: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");

  useEffect(() => {
    if (open && doc) {
      setLink("");
      setForm({ to: doc.recipient_email || "", subject: `Document: ${doc.title}`, message: doc.note ? `${doc.note}\n\n` : "" });
    }
  }, [open, doc]);

  if (!doc) return null;
  const configured = emailCfg?.email_configured;

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`${apiBase}/${doc.id}/forward`, form);
      toast.success("Email sent");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openInEmailApp = async () => {
    if (!form.to.trim()) return toast.error("Enter a recipient email");
    setBusy(true);
    try {
      const { data } = await api.post(`${apiBase}/${doc.id}/mailto`, { to: form.to, subject: form.subject });
      const l = `${BACKEND}/api${apiBase}/share/${data.token}`;
      setLink(l);
      const body = `${form.message || ""}\n\nDownload the document here:\n${l}\n`;
      window.location.href = `mailto:${encodeURIComponent(form.to)}?subject=${encodeURIComponent(form.subject || "Document")}&body=${encodeURIComponent(body)}`;
      toast.success("Opening your email app… the download link is included.");
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="forward-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Forward via Email</DialogTitle>
          <DialogDescription>
            {configured
              ? `${emailCfg?.mail_from ? "Sends from " + emailCfg.mail_from + " — " : ""}${doc.file?.filename} is attached.`
              : "Sends from your own email app with a secure download link to the document."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {!configured && (
            <div className="rounded-none border border-blue-400/40 bg-blue-50 p-3 text-xs text-blue-800 dark:bg-blue-950/20 dark:text-blue-300">
              No email setup needed — this opens <span className="font-medium">your own email app</span> with a secure download link already in the message.
            </div>
          )}
          <div className="space-y-1.5">
            <Label>To *</Label>
            <Input type="email" value={form.to} onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))} placeholder="name@example.com (comma-separate for multiple)" data-testid="forward-to" />
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} data-testid="forward-subject" />
          </div>
          <div className="space-y-1.5">
            <Label>Message</Label>
            <Textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} rows={configured ? 4 : 3} data-testid="forward-message" />
          </div>
          {!configured && link && (
            <div className="space-y-1.5">
              <Label>Secure download link</Label>
              <div className="flex items-center gap-2">
                <Input readOnly value={link} className="text-xs" data-testid="forward-link" />
                <Button size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} data-testid="forward-copy"><Copy className="h-4 w-4" /></Button>
              </div>
              <p className="text-xs text-muted-foreground">If your email app didn't open, copy this link into your email manually.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {configured ? (
            <Button onClick={send} disabled={busy} data-testid="forward-send">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Send</Button>
          ) : (
            <Button onClick={openInEmailApp} disabled={busy} data-testid="forward-mailto-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />} Open in Email App</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
